import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { listLookup } from '../../../api/masterData';
import { deleteAttachment, downloadAttachment, listAttachments, uploadAttachment } from '../../../api/leads';
import { downloadBlob } from '../../../utils/downloadBlob';

interface Step7Props {
  leadId: number | null;
}

const LEAD_DOCUMENT_TYPE_NAMES = [
  'NIC',
  'Passport',
  'O/L Results',
  'A/L Results',
  'Certificates',
  'CV',
  'English Certificate',
  'Other',
];

const DocumentSlot = ({
  leadId,
  documentTypeId,
  label,
  files,
  onChanged,
}: {
  leadId: number;
  documentTypeId: number;
  label: string;
  files: Array<{ id: number; originalName: string }>;
  onChanged: () => void;
}) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadAttachment(leadId, file, documentTypeId),
    onSuccess: onChanged,
  });
  const deleteMutation = useMutation({
    mutationFn: (attachmentId: number) => deleteAttachment(attachmentId),
    onSuccess: onChanged,
  });

  const handleFiles = (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (file) uploadMutation.mutate(file);
  };

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
      }}
      sx={{
        p: 2,
        border: '2px dashed',
        borderColor: dragOver ? 'primary.main' : 'divider',
        borderRadius: 2,
        bgcolor: dragOver ? 'action.hover' : 'transparent',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">{label}</Typography>
        <Tooltip title="Browse">
          <IconButton size="small" onClick={() => inputRef.current?.click()} disabled={uploadMutation.isPending}>
            <UploadFileIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      {files.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          Drag &amp; drop a file here, or click to browse.
        </Typography>
      ) : (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {files.map((f) => (
            <Stack key={f.id} direction="row" alignItems="center" spacing={1}>
              <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
                {f.originalName}
              </Typography>
              <Tooltip title="Preview / download">
                <IconButton
                  size="small"
                  onClick={async () => {
                    const { blob, filename } = await downloadAttachment(f.id);
                    downloadBlob(blob, filename);
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => deleteMutation.mutate(f.id)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export const Step7Documents = ({ leadId }: Step7Props) => {
  const queryClient = useQueryClient();

  const documentTypesQuery = useQuery({
    queryKey: ['lookup', '/master-data/document-types'],
    queryFn: () => listLookup('/master-data/document-types', { activeOnly: true }),
  });
  const attachmentsQuery = useQuery({
    queryKey: ['attachments', leadId],
    queryFn: () => listAttachments(leadId!),
    enabled: !!leadId,
  });

  const relevantTypes = (documentTypesQuery.data ?? [])
    .filter((t) => LEAD_DOCUMENT_TYPE_NAMES.includes(String(t.name)))
    .sort((a, b) => LEAD_DOCUMENT_TYPE_NAMES.indexOf(String(a.name)) - LEAD_DOCUMENT_TYPE_NAMES.indexOf(String(b.name)));

  if (!leadId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Complete Step 1 first — documents are uploaded directly against the lead.
      </Typography>
    );
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['attachments', leadId] });

  const otherAttachments = (attachmentsQuery.data ?? []).filter(
    (a) => !a.documentTypeId || !relevantTypes.some((t) => t.id === a.documentTypeId),
  );

  return (
    <Stack spacing={2}>
      {relevantTypes.map((type) => (
        <DocumentSlot
          key={type.id as number}
          leadId={leadId}
          documentTypeId={type.id as number}
          label={String(type.name)}
          files={(attachmentsQuery.data ?? []).filter((a) => a.documentTypeId === type.id)}
          onChanged={invalidate}
        />
      ))}

      {otherAttachments.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Other uploaded files
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
            {otherAttachments.map((a) => (
              <Chip key={a.id} size="small" label={a.originalName} icon={<InsertDriveFileOutlinedIcon />} />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
